import { ISizes } from '@interface/common.interface'
import { DECIMAL_NUMERIC_REGEX } from '@utility/checkvalidUrl'
import React, { ReactNode, useState } from 'react'
import Icon from '../Icon'

type IInputProps = {
  name?: string
  size?: ISizes
  label?: string | ReactNode
  autoFocus?: boolean
  accept?: string
  type?:
  | 'button'
  | 'text'
  | 'color'
  | 'date'
  | 'email'
  | 'datetime-local'
  | 'month'
  | 'number'
  | 'password'
  | 'search'
  | 'tel'
  | 'time'
  | 'url'
  | 'week'
  | 'file'
  numaricOnly?: boolean
  isRequired?: boolean
  hasInfo?: boolean
  infoText?: string
  placeholder?: string | ReactNode
  defaultValue?: string | number | Date
  value?: string | number | Date
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void
  onClick?: (event: any) => void
  onBlur?: (event: any) => void
  max?: string | number
  min?: string | number
  isError?: boolean
  helpText?: string
  isValid?: boolean
  errorMessage?: string
  noMargin?: boolean
  registerProperty?: any
  disabled?: boolean
  startIcon?: any
  endIcon?: any
  className?: string
  readOnly?: boolean
  /* Browsers autofill any bare `type="password"` box with the SIGNED-IN user's own saved
     credentials. On an admin form that edits somebody else, that silently stages the admin's
     password as the other person's -- so a password field here must pass `new-password`. */
  autoComplete?: string
}

const Input = ({
  name,
  size = 'sm',
  autoFocus,
  accept,
  label,
  type = 'text',
  numaricOnly,
  placeholder,
  defaultValue,
  value,
  isRequired,
  onChange,
  onFocus,
  onClick,
  onBlur,
  min,
  max,
  hasInfo,
  infoText,
  isError,
  helpText,
  isValid,
  startIcon,
  endIcon,
  errorMessage,
  noMargin,
  registerProperty,
  disabled = false,
  className,
  readOnly,
  autoComplete,
}: IInputProps) => {
  const [inputType, setInputType] = useState<string>(type)

  return (
    <div className={`w-100 ${className || ''} ${noMargin ? '' : 'mb-3'}`}>
      {label ? (
        <label className='form-label d-flex align-items-center gap-1'>
          <span className={isRequired ? 'required' : ''}>{label}</span>
          {hasInfo && <Icon icon='question-circle' hoverTitle={infoText} />}
        </label>
      ) : null}
      <div className='position-relative'>
        {/* start-0 is load-bearing: without it the absolute span falls back to left:auto
            (its static position) and ms-4 then pushes the icon out over the field's text.
            position-absolute must stay in the class list: erpboi-theme's
            `.position-relative > .position-absolute.input-start-icon` rule keys on it. */}
        {startIcon && (
          <span className='position-absolute top-50 start-0 ms-3 translate-middle-y input-start-icon'>
            {startIcon}
          </span>
        )}

        <input
          type={inputType}
          autoFocus={autoFocus}
          className={`form-control form-control-${size} ${isError ? 'is-invalid' : ''} ${isValid ? 'is-valid' : ''} ${
            startIcon ? 'has-start-icon' : ''
          }`}
          name={name}
          accept={accept}
          required={isRequired}
          defaultValue={defaultValue}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          onFocus={onFocus}
          onClick={onClick}
          onBlurCapture={onBlur}
          min={min}
          max={max}
          disabled={disabled}
          readOnly={readOnly}
          autoComplete={autoComplete}
          onKeyPress={
            type === 'number'
              ? (e) =>
                numaricOnly
                  ? !(e.charCode >= 48 && e.charCode <= 57) && e.preventDefault()
                  : !DECIMAL_NUMERIC_REGEX.test(e.key) && e.preventDefault()
              : null
          }
          onWheel={(e: any) => e.target.blur()}
          {...registerProperty}
        />

        {type === 'password' && (
          <span
            className='position-absolute top-50 end-0 me-3 translate-middle-y'
            role='button'
            title={inputType === 'password' ? 'Show password' : 'Hide password'}
          >
            <Icon
              icon={inputType === 'password' ? 'eye-slash' : 'eye'}
              size={20}
              onClick={() => setInputType((prev) => (prev === 'password' ? 'text' : 'password'))}
            />
          </span>
        )}

        {!!endIcon && (
          <span className='position-absolute top-50 end-0 mt-1 me-3 translate-middle-y'>
            {endIcon}
          </span>
        )}
      </div>
      {isError ? (
        <div className='invalid-feedback d-block'>{errorMessage}</div>
      ) : !!helpText ? (
        <div className='form-text'>{helpText}</div>
      ) : null}
    </div>
  )
}

export { Input }

